import React from 'react';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import { Link } from 'react-router-dom';
import { useReviewStore } from '@/lib/store';
import ReviewCard from '@/components/review-card';
import { Button } from '@/components/ui/button';
import { Briefcase, Flag, Users, TrendingUp, ArrowRight } from 'lucide-react';
import Footer from '@/components/Footer';
import LogoCarousel from '@/components/LogoCarousel';
import HowItWorks from '@/components/home/HowItWorks';
import ComparisonSection from '@/components/home/ComparisonSection';
import WhatRedFlagsDoes from '@/components/home/WhatRedFlagsDoes';
import ClaimCompanySection from '@/components/home/ClaimCompanySection';
import VideoSection from '@/components/home/VideoSection';

const Index = () => {
  const { reviews } = useReviewStore();
  const latestReviews = reviews.slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        <Hero />
        
        {/* Full-width Image Section with Quote Overlay */}
        <section className="w-full overflow-hidden">
          <div className="relative">
            <img 
              src="/lovable-uploads/f442a94c-cf4e-43ca-9694-3752c443ae19.png" 
              alt="Modern office workspace with desk and chair by window" 
              className="w-full max-h-[30vh] object-cover" 
            />
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6 max-w-4xl">
                <blockquote className="text-white text-2xl md:text-3xl font-bold leading-tight">
                  "1 in 4 people under 30 have been made redundant, stop relying on gut feel and use data to make better decisions"
                </blockquote>
              </div>
            </div>
          </div>
        </section>
        
        {/* NEW: What RedFlags Does section */}
        <WhatRedFlagsDoes />
        
        {/* NEW: Video Section */}
        <VideoSection />
        
        {/* How It Works section */}
        <HowItWorks />
        
        {/* NEW: Third Full-width Image Section */}
        <section className="w-full overflow-hidden">
          <div className="relative">
            <img 
              src="/lovable-uploads/4156e8d3-36d7-4cfc-902d-c8e871c4c284.png" 
              alt="Modern conference room with large table and chairs" 
              className="w-full max-h-[30vh] object-contain md:object-cover" 
            />
            <div className="absolute inset-0 bg-redflags-red/10"></div>
          </div>
        </section>
        
        {/* Features section */}
        <section className="py-20 px-6 bg-redflags-light/50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-3xl font-bold mb-4 text-redflags-dark">Why share your exit story?</h2>
              <p className="text-redflags-gray max-w-2xl mx-auto">
                Help job seekers avoid toxic workplaces and make informed career decisions.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <FeatureCard 
                icon={<Flag className="w-5 h-5" />}
                title="Identify Red Flags"
                description="Help others spot problematic workplace patterns before they accept a job offer."
              />
              <FeatureCard 
                icon={<Users className="w-5 h-5" />}
                title="Build Community"
                description="Join a community that values transparency in workplace experiences."
              />
              <FeatureCard 
                icon={<TrendingUp className="w-5 h-5" />}
                title="Drive Change"
                description="When companies see patterns in exit reasons, they may improve their workplace."
              />
            </div>
          </div>
        </section>
        
        {/* Latest reviews section */}
        {reviews.length > 0 && (
          <section className="py-20 px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex justify-between items-center mb-12">
                <h2 className="text-2xl font-bold text-redflags-dark">Latest Reviews</h2>
                <Link to="/reviews" className="text-redflags-red hover:text-redflags-red/90 font-medium flex items-center gap-1 text-sm">
                  View all
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {latestReviews.map(review => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            </div>
          </section>
        )}
        
        {/* Company Comparison section */}
        <ComparisonSection />
        
        {/* NEW: Claim Company Section */}
        <ClaimCompanySection />
        
        {/* Logo Carousel Section - moved here */}
        <LogoCarousel />
        
        {/* Second Full-width Image Section */}
        <section className="w-full overflow-hidden">
          <div className="relative">
            <img 
              src="/lovable-uploads/c9787579-a1a5-420f-81a1-7484e9f78190.png" 
              alt="Modern conference room with large table and chairs" 
              className="w-full max-h-[30vh] object-contain md:object-cover" 
            />
            <div className="absolute inset-0 bg-redflags-red/10"></div>
          </div>
        </section>
        
        {/* CTA section */}
        <section className="py-20 px-6 bg-redflags-red text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to share your experience?</h2>
            <p className="mb-8 text-white/80">
              Your story could help someone make a better career decision.
            </p>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white/10">
              <Link to="/add-review" className="flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Add your review
              </Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

// Helper component for features
const FeatureCard = ({ 
  icon, 
  title, 
  description 
}: { 
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-redflags-light/80 flex flex-col items-center text-center animate-slide-in">
    <div className="w-12 h-12 rounded-full bg-redflags-red/10 flex items-center justify-center text-redflags-red mb-4">
      {icon}
    </div>
    <h3 className="font-semibold text-lg mb-2 text-redflags-dark">{title}</h3>
    <p className="text-redflags-gray">{description}</p>
  </div>
);

export default Index;
